import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Shield, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const PrivacyPage = () => {
  const { t } = useTranslation();

  const sections = [
    { title: t('privacy.s1Title'),  content: t('privacy.s1Content')  },
    { title: t('privacy.s2Title'),  content: t('privacy.s2Content')  },
    { title: t('privacy.s3Title'),  content: t('privacy.s3Content')  },
    { title: t('privacy.s4Title'),  content: t('privacy.s4Content')  },
    { title: t('privacy.s5Title'),  content: t('privacy.s5Content')  },
    { title: t('privacy.s6Title'),  content: t('privacy.s6Content')  },
    { title: t('privacy.s7Title'),  content: t('privacy.s7Content')  },
    { title: t('privacy.s8Title'),  content: t('privacy.s8Content')  },
    { title: t('privacy.s9Title'),  content: t('privacy.s9Content')  },
    { title: t('privacy.s10Title'), content: t('privacy.s10Content') },
    { title: t('privacy.s11Title'), content: t('privacy.s11Content') },
  ];

  return (
    <>
      <Helmet>
        <title>{t('privacy.pageTitle')}</title>
        <meta name="description" content={t('privacy.metaDesc')} />
      </Helmet>

      <div className="pt-20 min-h-screen bg-white">
        {/* Hero */}
        <section className="section-sm bg-gradient-to-br from-primary-50 to-blue-50">
          <div className="container-custom text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full mb-6">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-4">{t('privacy.title')}</h1>
              <p className="text-xl text-gray-600 mb-4">{t('privacy.subtitle')}</p>
              <p className="text-sm text-gray-500">{t('privacy.lastUpdated')}</p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="section">
          <div className="container-custom">
            <div className="max-w-4xl mx-auto">
              {/* Introduction */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-12"
              >
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('privacy.introTitle')}</h2>
                <p className="text-gray-700 leading-relaxed">{t('privacy.introText')}</p>
              </motion.div>

              {/* Sections */}
              <div className="space-y-8">
                {sections.map((section, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-white rounded-xl p-8 border border-gray-200 shadow-sm"
                  >
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h2>
                    <div
                      className="text-gray-700 leading-relaxed prose prose-primary max-w-none"
                      dangerouslySetInnerHTML={{ __html: section.content }}
                    />
                  </motion.div>
                ))}
              </div>

              {/* Contact CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="mt-12 bg-gradient-primary text-white rounded-xl p-8 text-center"
              >
                <Mail className="w-12 h-12 mx-auto mb-4 text-blue-200" />
                <h3 className="text-2xl font-bold mb-2">{t('privacy.ctaTitle')}</h3>
                <p className="text-blue-100 mb-6">{t('privacy.ctaDesc')}</p>
                <Link to="/contact" className="btn btn-white">{t('privacy.ctaBtn')}</Link>
              </motion.div>

              {/* Related Links */}
              <div className="mt-12 grid md:grid-cols-2 gap-6">
                <Link
                  to="/terms"
                  className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all text-center"
                >
                  <h4 className="font-bold text-gray-900 mb-2">{t('privacy.relTermsTitle')}</h4>
                  <p className="text-sm text-gray-600">{t('privacy.relTermsDesc')}</p>
                </Link>
                <Link
                  to="/security"
                  className="bg-gray-50 rounded-xl p-6 border border-gray-200 hover:border-primary-300 hover:shadow-md transition-all text-center"
                >
                  <h4 className="font-bold text-gray-900 mb-2">{t('privacy.relSecurityTitle')}</h4>
                  <p className="text-sm text-gray-600">{t('privacy.relSecurityDesc')}</p>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PrivacyPage;
